'use client';

import React, { ReactNode } from 'react';

interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline';
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  loadingText?: string;
  className?: string;
  children: ReactNode;
  disableHover?: boolean;
}

const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  variant = 'primary',
  icon,
  iconPosition = 'left',
  loadingText,
  className = '',
  children,
  disabled,
  disableHover = false,
  ...props
}) => {
  // Determine button styles based on variant
  const getButtonStyles = () => {
    const hoverPrefix = disableHover ? '' : 'hover:';
    switch (variant) {
      case 'primary':
        return `bg-[#151C28] text-white ${hoverPrefix}bg-[#232b3a]`;
      case 'secondary':
        return `bg-white border border-gray-300 text-gray-700 ${hoverPrefix}bg-gray-50`;
      case 'danger':
        return `bg-[#991B15] text-white ${hoverPrefix}bg-red-700`;
      case 'outline':
        return `border border-gray-300 text-gray-700 ${hoverPrefix}bg-gray-50`;
      default:
        return `bg-[#151C28] text-white ${hoverPrefix}bg-[#232b3a]`;
    }
  };

  // Spinner component
  const Spinner = () => (
    <svg className="animate-spin h-4 w-4 mr-2 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
    </svg>
  );

  return (
    <button
      className={`px-4 cursor-pointer py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${getButtonStyles()} ${loading || disabled ? 'opacity-70 cursor-not-allowed' : ''} ${className}`}
      disabled={loading || disabled}
      {...props}
    >
      {loading && <Spinner />}
      {!loading && icon && iconPosition === 'left' && icon}
      {loading && loadingText ? loadingText : children}
      {!loading && icon && iconPosition === 'right' && icon}
    </button>
  );
};

export default LoadingButton;